// The page editor's one motion language: Orbit's ease, quick durations,
// transform and opacity only, and nothing at all for people who ask their
// system for reduced motion.
import { useLayoutEffect, useRef, type RefObject } from 'react';

export const EASE = 'cubic-bezier(.2,0,0,1)';

/** The person asked for reduced motion (checked in the element's own window,
 *  so the canvas frame answers for itself). */
export function prefersReducedMotion(el?: Element | null): boolean {
  const view = el?.ownerDocument?.defaultView ?? (typeof window !== 'undefined' ? window : null);
  try { return !!view?.matchMedia?.('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

/** Rows of a list glide to their new place when the order changes (FLIP), and
 *  rows that are new fade in. Children marked data-flip-key take part; `order`
 *  is anything that changes when the list does (e.g. the keys joined). The
 *  first render never animates. */
export function useFlip(listRef: RefObject<HTMLElement | null>, order: string) {
  const prev = useRef<Map<string, number> | null>(null);
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const rows = [...list.querySelectorAll<HTMLElement>(':scope > [data-flip-key]')];
    const now = new Map(rows.map((r) => [r.dataset.flipKey ?? '', r.offsetTop]));
    const before = prev.current;
    prev.current = now;
    if (!before || prefersReducedMotion(list)) return;
    for (const r of rows) {
      if (typeof r.animate !== 'function') continue;
      const was = before.get(r.dataset.flipKey ?? '');
      if (was === undefined) {
        r.animate([{ opacity: 0, transform: 'translateY(-4px)' }, { opacity: 1, transform: 'none' }], { duration: 180, easing: EASE });
        continue;
      }
      const dy = was - (now.get(r.dataset.flipKey ?? '') ?? was);
      if (Math.abs(dy) > 0.5) r.animate([{ transform: `translateY(${dy}px)` }, { transform: 'none' }], { duration: 200, easing: EASE });
    }
  }, [order, listRef]);
}
